<template>
  <el-row justify="center" align="middle" class="h-full">
    <el-card v-loading="loading">
      <!-- TEMP: временно открываем логин/пароль и на проде до полной раскатки SSO. -->
      <form-generator
        ref="authForm"
        :fields="fields"
        label-position="top"
        autocomplete
      />
      <el-row justify="end">
        <el-button type="primary" @click="submitForm">
          {{ $t('action.send') }}
        </el-button>
      </el-row>
      <el-divider>{{ $t('action.orContinueWith') }}</el-divider>

      <el-row justify="center" class="auth-buttons-container">
<!--        <el-col :span="24" class="auth-button-wrapper">-->
<!--          <el-button-->
<!--            type="info"-->
<!--            class="auth-button"-->
<!--            @click="loginWithAitu"-->
<!--          >-->
<!--            {{ $t('action.aituLogin') }}-->
<!--          </el-button>-->
<!--        </el-col>-->

        <el-col :span="24" class="auth-button-wrapper">
          <el-button
            type="warning"
            class="auth-button"
            @click="loginWithIdp"
          >
            <el-icon class="button-icon"><UserFilled /></el-icon>
            {{ $t('action.idpLogin') }}
          </el-button>
        </el-col>

<!--        <el-col :span="24" class="auth-button-wrapper">-->
<!--          <el-button-->
<!--            type="primary"-->
<!--            class="auth-button"-->
<!--            @click="loginWithDigitalId"-->
<!--          >-->
<!--            Digital ID-->
<!--          </el-button>-->
<!--        </el-col>-->
        
        <el-col :span="24" class="auth-button-wrapper">
          <el-button
            type="success"
            class="auth-button"
            :disabled="!ncaLayerAvailable"
            @click="loginWithCertificate"
          >
            <el-icon class="button-icon"><Key /></el-icon>
            {{ $t('action.ecpLogin') }}
          </el-button>
        </el-col>
      </el-row>

      <el-row justify="center" class="mt-4 status-row">
        <div class="status-container">
          <div class="status-tags">
            <el-tag :type="ncaLayerAvailable ? 'success' : 'danger'" size="small">
              NCALayer: {{ ncaLayerAvailable ? $t('ncaLayer.connected') : $t('ncaLayer.notConnected') }}
            </el-tag>
            <el-tag
              v-if="ncaLayerAvailable"
              type="info"
              size="small"
              style="margin-left: 8px;"
            >
              {{ ncaLayerVersion }}
            </el-tag>
          </div>
        </div>
      </el-row>

      <el-row v-if="selectedCertificate" justify="center" class="mt-4">
        <el-card style="width: 100%; max-width: 500px;">
          <template #header>
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span>{{ $t('ncaLayer.selectedCertificate') }}</span>
              <el-tag type="success" size="small">{{ $t('ncaLayer.readyForAuth') }}</el-tag>
            </div>
          </template>
          <div class="certificate-info">
            <div class="info-row">
              <strong>{{ $t('ncaLayer.owner') }}</strong> {{ selectedCertificate.subjectCn }}
            </div>
            <div class="info-row" v-if="selectedCertificate.serialNumber">
              <strong>{{ $t('ncaLayer.serialNumber') }}</strong> {{ selectedCertificate.serialNumber }}
            </div>
            <div class="info-row" v-if="selectedCertificate.issuerCn">
              <strong>{{ $t('ncaLayer.issuer') }}</strong> {{ selectedCertificate.issuerCn }}
            </div>
            <div class="info-row" v-if="selectedCertificate.certNotBefore">
              <strong>{{ $t('ncaLayer.validFrom') }}</strong> {{ formatDate(selectedCertificate.certNotBefore) }}
            </div>
            <div class="info-row" v-if="selectedCertificate.certNotAfter">
              <strong>{{ $t('ncaLayer.validTo') }}</strong> {{ formatDate(selectedCertificate.certNotAfter) }}
            </div>
          </div>
        </el-card>
      </el-row>

      <el-row v-if="!ncaLayerAvailable" justify="center" class="mt-4">
        <div class="ecp-not-connected">
          <el-alert
            :title="$t('ncaLayer.notConnectedTitle')"
            type="warning"
            show-icon
            :closable="false"
          >
            <template #default>
              <div class="mb-2">
                <strong>{{ $t('ncaLayer.connectionProblem') }}</strong><br>
                {{ $t('ncaLayer.requirementsTitle') }}
                <ul style="margin: 8px 0; padding-left: 20px;">
                  <li>{{ $t('ncaLayer.installNCALayer') }}</li>
                  <li>{{ $t('ncaLayer.connectDevice') }}</li>
                  <li>{{ $t('ncaLayer.checkPort') }}</li>
                </ul>
              </div>
              <div style="margin-top: 12px;">
                <el-button
                  @click="checkNCALayerConnection"
                  size="small"
                  type="primary"
                >
                  {{ $t('ncaLayer.retryConnection') }}
                </el-button>
                <el-link
                  :href="`https://pki.gov.kz/docs/nl_${locale === 'ru' ? 'ru' : 'kaz'}/windows/`"
                  target="_blank"
                  type="primary"
                  style="margin-left: 12px;"
                >
                  {{ $t('ncaLayer.downloadNCALayer') }}
                </el-link>
              </div>
            </template>
          </el-alert>
        </div>
      </el-row>
    </el-card>
  </el-row>
</template>

<script setup lang="ts">
import {computed, ref, onMounted} from 'vue'
import {ElNotification} from 'element-plus'
import {useRouter} from 'vue-router'
import {useI18n} from 'vue-i18n'
import {Key, UserFilled} from '@element-plus/icons-vue'
import FormGenerator from '@/components/VFormGenerator.vue'
import type {FormConfigField} from '@/types/Form'
import {useAuthStore} from '@/stores/auth'
import init from '@/lib/init'
import {NCALayerClient} from '@/utils/ncalayer-client'
import {accessTokenStorage} from '@/helpers/storage'

interface CertificateInfo {
  subjectCn: string
  subjectDn: string
  serialNumber: string
  issuerCn: string
  issuerDn: string
  certNotBefore: string
  certNotAfter: string
  algorithm: string
  keyId: string
  pem: string
}

interface Props {
  verifyUrl?: string
}

const props = withDefaults(defineProps<Props>(), {
  verifyUrl: import.meta.env.VITE_API_URL_CORE + '/api/ncanode/xml/verify',
})

const {locale, t} = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const loading = ref(false)
const ncaLayerAvailable = ref(false)
const ncaLayerVersion = ref('')
const selectedCertificate = ref<CertificateInfo | null>(null)

let ncaLayerClient: any = null

const fields = computed((): FormConfigField[] => ([
  {
    label: t('label.login'),
    model: 'login',
    type: 'input',
    name: 'login',
    placeholder: t('placeholder.login'),
    validation: [
      {required: true, message: t('validation.required'), trigger: 'change'},
    ],
    props: {
      controls: false,
    },
  },
  {
    label: t('label.password'),
    model: 'password',
    type: 'input',
    name: 'password',
    placeholder: t('placeholder.password'),
    validation: [
      {required: true, message: t('validation.required'), trigger: 'change'},
    ],
    props: {
      showPassword: true,
    },
  },
]))

const authForm = ref()

onMounted(async () => {
  await initializeNCALayer()
})

const initializeNCALayer = async () => {
  try {
    ncaLayerClient = new NCALayerClient()

    const version = await ncaLayerClient.connect()
    ncaLayerAvailable.value = true
    ncaLayerVersion.value = `v${version}`

    ElNotification({
      type: 'success',
      message: t('ncaLayer.connectedVersion', {version}),
      position: 'bottom-right',
      duration: 3000,
    })

  } catch (error: any) {
    ncaLayerAvailable.value = false
    console.warn('NCALayer is not available:', error.message)
  }
}

const checkNCALayerConnection = async () => {
  try {
    loading.value = true

    ncaLayerClient = null
    ncaLayerAvailable.value = false
    ncaLayerVersion.value = ''
    selectedCertificate.value = null
    await initializeNCALayer()

  } catch (error: any) {
    console.error('Error connecting to NCALayer:', error)
    ElNotification({
      type: 'error',
      message: t('ncaLayer.connectionFailed'),
      position: 'bottom-right',
    })
  } finally {
    loading.value = false
  }
}

const formatDate = (timestamp: string): string => {
  if (!timestamp) return ''
  const date = new Date(parseInt(timestamp))
  return date.toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const createAuthXmlDocument = (): string => {
  const timestamp = new Date().toISOString()
  const sessionId = `auth_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  return `<?xml version="1.0" encoding="UTF-8"?>
<AuthRequest>
  <SessionId>${sessionId}</SessionId>
  <Timestamp>${timestamp}</Timestamp>
  <ClientInfo>
    <UserAgent>${navigator.userAgent}</UserAgent>
    <Language>${locale.value}</Language>
    <URL>${window.location.href}</URL>
  </ClientInfo>
  <Challenge>auth_challenge_${Date.now()}</Challenge>
</AuthRequest>`
}

const selectStorageType = (): string => {
  return 'PKCS12'
}

const loginWithCertificate = async () => {
  if (!ncaLayerClient || !ncaLayerAvailable.value) {
    ElNotification({
      type: 'error',
      message: t('ncaLayer.notRunning'),
      position: 'bottom-right',
    })
    return
  }

  try {
    loading.value = true
    selectedCertificate.value = null

    const storageType = selectStorageType()
    const xmlToSign = createAuthXmlDocument()

    ElNotification({
      type: 'info',
      message: t('ncaLayer.selectCertificate'),
      position: 'bottom-right',
      duration: 3000,
    })

    // Подписываем XML
    const signedXml = await ncaLayerClient.signXml(
      storageType,
      xmlToSign,
      'SIGNATURE', // Тип ключа для подписи
      '', // tbsElementXPath - пустой для подписи всего документа
      '', // signatureParentElementXPath - пустой для добавления в корень
    )

    ElNotification({
      type: 'success',
      message: t('ncaLayer.xmlSigned'),
      position: 'bottom-right',
      duration: 3000,
    })

    // Верифицируем подпись через Middleware API -> Ncanode
    await verifXmlSignature(signedXml)

  } catch (error: any) {
    console.error('Error authenticating with certificate:', error)
    
    ElNotification({
      type: 'error',
      message: error.message || t('ncaLayer.authenticationFailed'),
      position: 'bottom-right',
      duration: 8000,
    })

    selectedCertificate.value = null
  } finally {
    loading.value = false
  }
}


// Верификация XML подписи через Laravel/ncanode API
const verifXmlSignature = async (signedXml: string): Promise<void> => {
  try {
    const response = await fetch(props.verifyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        xml: signedXml,
        revocationCheck: ['OCSP'],
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Ошибка HTTP при верификации:', response.status, errorText)
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    const result = await response.json()

    if (result.data?.success === true && result.data?.accessToken) {
      accessTokenStorage.set(result.data.accessToken)
      authStore.accessToken = result.data.accessToken
      
      ElNotification({
        type: 'success',
        message: t('notification.success.auth'),
        position: 'bottom-right',
        duration: 3000,
      })

      await init()
      await router.push('/organizations')
    } else if (result.data?.success === false) {
      throw new Error(result.message || t('ncaLayer.authenticationFailed'))
    } else {
      throw new Error(t('ncaLayer.unexpectedResponse'))
    }
  } catch (error: any) {
    console.error('Ошибка верификации XML подписи через Laravel API:', error)
    throw new Error(`Ошибка верификации подписи: ${error.message}`)
  }
}

const submitForm = async () => {
  try {
    await authForm.value.validateForm(async (valid: boolean) => {
      if (valid) {
        loading.value = true
        const res = await authStore.authUser(authForm.value.formData)

        if (res.message) {
          ElNotification({
            type: res.type,
            duration: res.type === 'success' ? 3000 : 0,
            message: t(res.message),
            position: 'bottom-right',
          })
        }

        if (authStore.isAuth) {
          await init()
          await router.push('/organizations')
        }

        loading.value = false
      }
    })
  } catch (error) {
    console.error(`Error in view -> ${error?.toString()}`)
  } finally {
    loading.value = false
  }
}

const loginWithAitu = async () => {
  try {
    loading.value = true

    const res = await authStore.authWithAitu()

    ElNotification({
      type: res.type,
      duration: res.type === 'success' ? 3000 : 0,
      message: t(res.message),
      position: 'bottom-right',
    })

    if (res.type === 'success' && res.data?.redirect) {
      window.location.href = res.data.redirect
    }
  } catch (error) {
    console.error(`Error in loginWithAitu -> ${error?.toString()}`)
  } finally {
    loading.value = false
  }
}

const loginWithIdp = async () => {
  try {
    loading.value = true

    const res = await authStore.createIdpState(locale.value)

    if (res.type === 'success' && res.data?.authUrl) {
      try {
        new URL(res.data.authUrl)
        window.location.href = res.data.authUrl
        setTimeout(() => {
          const popup = window.open(res.data.authUrl, '_self')
          if (!popup) {
            window.open(res.data.authUrl, '_blank')
          }
        }, 1000)
        
        return
      } catch (error) {
        console.error('Invalid URL:', error)
        ElNotification({
          type: 'error',
          duration: 0,
          message: 'Некорректный URL для авторизации',
          position: 'bottom-right',
        })
      }
    } else {
      ElNotification({
        type: res.type || 'error',
        duration: 0,
        message: t(res.message || 'error.general'),
        position: 'bottom-right',
      })
    }
  } catch (error) {
    console.error(`Error in loginWithIdp -> ${error?.toString()}`)
    ElNotification({
      type: 'error',
      duration: 0,
      message: t('error.general'),
      position: 'bottom-right',
    })
  }
  
  loading.value = false
}

const loginWithDigitalId = async () => {
  try {
    loading.value = true

    const res = await authStore.createDigitalIdState()

    if (res.type === 'success' && res.data?.authUrl) {
      try {
        new URL(res.data.authUrl)
        window.location.href = res.data.authUrl
        return
      } catch (error) {
        console.error('Invalid URL:', error)
        ElNotification({
          type: 'error',
          duration: 0,
          message: t('error.digitalId.invalidUrl'),
          position: 'bottom-right',
        })
      }
    } else {
      ElNotification({
        type: res.type || 'error',
        duration: 0,
        message: t(res.message || 'error.general'),
        position: 'bottom-right',
      })
    }
  } catch (error) {
    console.error(`Error in loginWithDigitalId -> ${error?.toString()}`)
    ElNotification({
      type: 'error',
      duration: 0,
      message: t('error.general'),
      position: 'bottom-right',
    })
  }
  
  loading.value = false
}
</script>

<style scoped>
:deep(.el-card) {
  border-radius: 16px;
  width: 360px;
  max-width: 360px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.04);
  border: 1px solid var(--el-border-color-lighter);
  backdrop-filter: blur(10px);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

:deep(.el-form-item__label) {
  position: relative;
}

:deep(.el-form-item__label::before) {
  content: none !important;
}

:deep(.el-form-item__label::after) {
  content: '*';
  color: var(--el-color-danger);
  margin-left: 4px;
}

:deep(.el-form-item:not(.is-required) .el-form-item__label::after) {
  content: none;
}

:deep(.el-form-item) {
  margin-bottom: 12px;
}

:deep(.el-form-item__label) {
  margin-bottom: 4px;
  padding-bottom: 0;
}

.auth-button-wrapper {
  display: flex;
  justify-content: center;
}

.auth-button {
  width: 100%;
  max-width: 300px;
  border-radius: 6px;
}

.ecp-not-connected {
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
}

.ecp-not-connected :deep(.el-alert) {
  border-radius: 6px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.ecp-not-connected :deep(.el-alert__content) {
  flex: 1;
  min-width: 0;
}

.ecp-not-connected :deep(.el-link) {
  word-break: break-all;
}

.mt-4 {
  margin-top: 1rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.certificate-info {
  font-size: 14px;
}

.info-row {
  margin-bottom: 8px;
  padding: 4px 0;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.info-row:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.info-row strong {
  display: inline-block;
  width: 140px;
  color: var(--el-text-color-regular);
  font-weight: 600;
}

.auth-buttons-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.auth-button {
  width: 100%;
  border-radius: 6px;
}

.status-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.status-tags {
  display: flex;
  align-items: center;
  justify-content: center;
}

.check-button-small {
  border-radius: 4px;
  padding: 2px 12px;
  background-color: var(--el-text-color-placeholder);
  border: none;
}

.button-icon {
  margin-right: 8px;
}

@media (max-width: 768px) {
  .auth-button {
    width: 100%;
  }
  
  .status-container {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
